import { useLocation, useSearch } from '@tanstack/react-router';

import {
  COURSE_PLAYER_PATH,
  LEARNING_ACTIVITY_EDIT_PATH,
  LEARNING_ACTIVITY_PREVIEW_PATH,
  PBL_PREVIEW_PATH,
  PLAYER_ACTIVITY_PATH,
  PLAYER_ASSIGNMENT_PATH,
  PLAYER_PATH,
  PLENARY_EDIT_PATH,
  PLENARY_PREVIEW_PATH,
  STUDIO_COURSE_PREVIEW_PATH,
  STUDIO_PATH,
  STUDIO_PBL_EDIT_PATH,
  STUDIO_SNAPSHOT_HISTORY_PARTIAL_PATH
} from '../common/consts/paths';
import { useLearningObjectCommonData } from '../components/providers/LearningObjectCommonDataProvider';

export const useWorkModeInternal = () => {
  const { pathname } = useLocation();
  const search = useSearch({ strict: false });
  const searchParams = new URLSearchParams(
    Object.entries(search)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)])
  );

  const page = searchParams?.get('page');
  const mockTest = searchParams?.get('mockTest'); // TODO: Change mocking test state to some other long-term solution
  const mode = searchParams?.get('mode');
  const isThemePreview = !!searchParams?.get('themeId');

  const { assignmentId } = useLearningObjectCommonData();

  const isPlayer = pathname.includes(PLAYER_PATH) && mode !== 'preview';
  const isPlayerActivity =
    pathname.includes(PLAYER_ACTIVITY_PATH) &&
    !pathname.includes(PLAYER_ASSIGNMENT_PATH) &&
    mode !== 'preview' &&
    !assignmentId;
  const isCourseStudentAssignment = pathname.includes(COURSE_PLAYER_PATH) && searchParams?.get('studentId');

  // `isPlayerAssignment` is true either when the path indicates it's the assignment page or the `assignmentId` is explicitly set
  const isPlayerAssignment =
    Boolean(pathname.includes(PLAYER_ASSIGNMENT_PATH) || isCourseStudentAssignment) || Boolean(assignmentId);
  const isPlayerAssignmentSubmitPage = isPlayerAssignment && page === 'submit';
  const isPlayerAssignmentSummaryPage = isPlayerAssignment && page === 'summary';
  // A standalone activity's summary page: the 720 exam's summary feedback after "Check Me" (PBI 537642).
  const isPlayerActivitySummaryPage = isPlayerActivity && page === 'summary';
  const isPlayerAssignmentTest = isPlayerAssignment && mockTest === 'true';
  const isSnapshotHistory = pathname.includes(STUDIO_SNAPSHOT_HISTORY_PARTIAL_PATH);
  const isStudioPreview =
    pathname.includes(PLENARY_PREVIEW_PATH) ||
    pathname.includes(LEARNING_ACTIVITY_PREVIEW_PATH) ||
    pathname.includes(PBL_PREVIEW_PATH) ||
    pathname.includes(STUDIO_COURSE_PREVIEW_PATH) ||
    (pathname.includes(PLAYER_PATH) && mode === 'preview') ||
    isThemePreview;
  const isStudio =
    (pathname.includes(STUDIO_PATH) && !pathname.includes(STUDIO_SNAPSHOT_HISTORY_PARTIAL_PATH)) || isStudioPreview;
  const isStudioEdit =
    (pathname.includes(LEARNING_ACTIVITY_EDIT_PATH || PLENARY_EDIT_PATH || STUDIO_PBL_EDIT_PATH) ||
      /\/studio\/.*\/edit\//.test(pathname)) &&
    !pathname.includes(STUDIO_SNAPSHOT_HISTORY_PARTIAL_PATH) &&
    !isThemePreview; //when themeId exists, we are on theme preview
  const isStudioMainPage = pathname === STUDIO_PATH;
  const isPlayerSpecialPage =
    isPlayerAssignmentSubmitPage || isPlayerAssignmentSummaryPage || isPlayerActivitySummaryPage;

  return {
    isPlayer,
    isPlayerActivity,
    isPlayerActivitySummaryPage,
    isPlayerAssignment,
    isPlayerAssignmentSubmitPage,
    isPlayerAssignmentSummaryPage,
    isPlayerAssignmentTest,
    isSnapshotHistory,
    isStudio,
    isStudioMainPage,
    isStudioEdit,
    isStudioPreview,
    isPlayerSpecialPage,
    isThemePreview
  };
};
